import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Link,
  Card,
  CardContent,
  TextField,
} from "@mui/material";
import { showToast } from "../../components/toastNotification/index";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import loginService from "../../services/login-service";
import i18n from "../../i18n";
import { useDispatch } from "react-redux";
import { setMultiLingual } from "../../globalState/actions/headerAction";
import { loginDetails } from "../../globalState/actions/authAction";
import { getDeviceFingerprint } from "../../utils/deviceFingerprint";

const LoginOTP: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { email, password } = location.state as {
    email: string;
    password: string;
  };
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState<number>(60);
  const [canResend, setCanResend] = useState<boolean>(false);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);

  const handleChange = (value: string, index: number) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLDivElement>,
    index: number
  ) => {
    const target = e.target as HTMLInputElement;
    if (e.key === "Enter" && otp.join("").length === 6) {
      handleSubmit();
    } else if (e.key === "Backspace" && !target.value && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      prevInput?.focus();
    }
  };
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("Text").trim();
  
    if (/^\d{1,6}$/.test(pasteData)) {
      const newOtp = pasteData.slice(0, 6).split("");
      const updatedOtp = [...otp];
  
      for (let i = 0; i < newOtp.length; i++) {
        updatedOtp[i] = newOtp[i];
      }
  
      setOtp(updatedOtp);
  
      // Focus next empty field after paste
      const nextEmptyIndex = updatedOtp.findIndex((val) => val === "");
      if (nextEmptyIndex !== -1) {
        const nextInput = document.getElementById(`otp-input-${nextEmptyIndex}`);
        nextInput?.focus();
      }
    }
  };
  
  const startTimer = () => {
    if (intervalId) clearInterval(intervalId);
    setCanResend(false);
    setTimer(60);

    const id = setInterval(() => {
      setTimer((prev) => {
        if (prev === 1) {
          clearInterval(id);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    setIntervalId(id);
  };

  useEffect(() => {
    startTimer();

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  ///// API calling function start
  //// handle submit Otp for login
  const handleSubmit = async () => {
    const otpCode = otp.join("");
    const deviceFingerprint = await getDeviceFingerprint();

    const SignUpInputData = {
      email,
      password,
      otp: otpCode,
      device_fingerprint: deviceFingerprint,
    };

    loginService
      .getLoginOTPVerifyAPI(SignUpInputData)
      .then((response) => {
        if (response && response?.data.message === "Login Successful") {
          let loginData = {};
          if (response?.data.user.invite_status === "Rejected") {
            loginData = {
              ...response?.data.user,
              invite_from_org: false,
              isUserLoggedIn: true,
            };
          } else {
            loginData = {
              ...response?.data.user,
              isUserLoggedIn: true,
            };
          }
          i18n.changeLanguage(response?.data.user.language);
          dispatch(setMultiLingual(response?.data.user.language));
          dispatch(loginDetails(loginData));
          localStorage.setItem("token", response?.data.access_token);
          navigate("/");
        } else {
          showToast(t(response?.data.message, "An error occurred."), "error");
        }
      })
      .catch((error) => {
        console.error(t("OTP Verification Error:"), error);
      });
  };

  //// handle resend OTP code
  const handleResendCode = () => {
    const inputData = {
      email,
      password,
    };
    loginService
      .userLogin_Otp(inputData)
      .then((response) => {
        if (
          response?.data.message === "OTP sent successfully. Verify to proceed."
        ) {
          showToast(t("A new OTP code has been sent."), "success");
          setTimer(60);
          setCanResend(false);
          startTimer();
        } else {
          showToast(t(response?.data.message), "error");
        }
      })
      .catch((error) => {
        showToast(t(error), "error");
      });
  };

  ///// API calling function end
  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            mb: 13,
            mt: 3,
          }}
        >
          <Card
            sx={{
              minWidth: { xs: 200, sm: 300, md: 400 },
              maxWidth: { xs: 350, sm: 500, md: 500 },
              m: 2,
              py: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              borderRadius: "40px",
              border: "1px solid #DFDFDF",
              opacity: "1px",
              boxShadow: "none",
            }}
          >
            <CardContent sx={{ textAlign: "center", width: "80%" }}>
              <Typography
                variant="h4"
                color="#0464AB;"
                gutterBottom
                sx={{
                  fontSize: 24,
                  letterSpacing: "1px",
                  marginBottom: "0px",
                }}
                className="h4"
              >
                {t("Verification")}
              </Typography>
              <Typography
                variant="subtitle1"
                gutterBottom
                fontSize={12}
                fontWeight={540}
                sx={{
                  borderBottom: "1px solid #DFDFDF",
                  p: 1,
                  color: "#000000",
                }}
              >
                {t("Enter your OTP code number")}
              </Typography>

              <Box sx={{ pt: 1 }}>
                <form>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    mb={2}
                    sx={{ margin: "10px" }}
                  >
                    {otp.map((value, index) => (
                      <TextField
                        key={index}
                        id={`otp-input-${index}`}
                        type="text"
                        inputProps={{
                          maxLength: 1,
                          style: { textAlign: "center", fontSize: "24px" },
                        }}
                        value={value}
                        onChange={(e) => handleChange(e.target.value, index)}
                        onKeyDown={(e) => handleKeyPress(e, index)}
                        onPaste={handlePaste}
                        variant="outlined"
                        sx={{
                          width: 50,
                          height: 50,
                          mx: 0.5,
                          "& .MuiInputBase-input.MuiOutlinedInput-input": {
                            height: "0.4375em",
                            padding: {
                              xs: "16.5px 1px",
                              sm: "16.5px 14px",
                              md: "16.5px 14px",
                            },
                          },
                        }}
                      />
                    ))}
                  </Box>
                  <Typography
                    color="#575757;"
                    gutterBottom
                    sx={{
                      letterSpacing: "1px",
                      marginBottom: "0px",
                    }}
                  >
                    {t("Device will be remembered for 7 days")}
                  </Typography>
                  <Button
                    variant="contained"
                    disableElevation
                    disabled={otp.join("").length !== 6}
                    onClick={handleSubmit}
                    sx={{
                      borderRadius: "21px",
                      backgroundColor: "#0464AB",
                      width: 120,
                      height: 35,
                      fontSize: "14px",
                      fontWeight: "600",
                      textTransform: "none",
                      marginTop: "10px",
                      "&:hover": {
                        backgroundColor: "#FBB522",
                        color: "#022E4C",
                        fontWeight: "600",
                        borderColor: "#FBB522",
                      },
                    }}
                  >
                    {t("Verify")}
                  </Button>
                  {canResend ? (
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      fontSize={12}
                      sx={{
                        pb: 2,
                        fontWeight: 600,
                        color: "#575757",
                        marginTop: "30px",
                        lineHeight: 2,
                      }}
                    >
                      {t("Didn't receive a code?")}
                      <Box
                        component="span"
                        sx={{ display: "block" }}
                      >
                        <Link
                          onClick={handleResendCode}
                          sx={{
                            color: "#1976d2",
                            fontWeight: "bold",
                            cursor: "pointer",
                          }}
                        >
                          {t("Resend Code")}
                        </Link>
                      </Box>
                    </Typography>
                  ) : null}
                </form>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </>
  );
};

export default LoginOTP;
